import React, { useContext } from 'react'
import moment from 'moment'
import { Alert, Button, Chip, LinearProgress, Stack } from '@mui/material'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import CustomDataGrid from '../../../../components/DataGrid/CustomDataGrid'
import { TenantSettingsContext } from '../../../../App'
import { getCurrencySymbol } from '../../../../helpers/currency'
import {
  SectionHeader,
  dateTone,
  formatDate,
} from '../../vehicles/VehicleComponents/SectionKit'
import { AssetContext } from './SingleAsset'

// Maintenance logs for an asset flagged as requiring servicing.
const ServicingSection = () => {
  const { asset, maintenanceLogs, maintenanceLoading, openDialog } =
    useContext(AssetContext)
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const currency = getCurrencySymbol(orgSettings?.currency)

  const expiry = asset.latestMaintenanceLog?.expiry
  const overdue = expiry && moment(expiry).isBefore(moment().startOf('day'))
  const caption = !expiry
    ? 'No maintenance has been recorded for this asset yet'
    : overdue
      ? `Service was due ${formatDate(expiry)} — this asset is overdue`
      : `Next service due ${formatDate(expiry)}`

  const rows = [...(maintenanceLogs ?? [])].sort(
    (a, b) => new Date(b.maintenanceDate) - new Date(a.maintenanceDate),
  )

  return (
    <Stack spacing={1.5}>
      <SectionHeader
        title="Servicing"
        caption={caption}
        action={
          <Button
            size="small"
            variant="outlined"
            startIcon={<BuildOutlinedIcon />}
            onClick={() => openDialog('maintenance')}
          >
            Record Maintenance
          </Button>
        }
      />
      {maintenanceLoading ? (
        <LinearProgress />
      ) : rows.length === 0 ? (
        <Alert severity="info">
          No maintenance logs yet. Record the first service or repair to start
          tracking when this asset is next due.
        </Alert>
      ) : (
        <CustomDataGrid
          rows={rows}
          getRowId={(row) => row._id}
          columns={[
            {
              field: 'maintenanceDate',
              headerName: 'Date',
              minWidth: 110,
              valueGetter: (value) => (value ? moment(value).toDate() : null),
              renderCell: ({ value }) =>
                value ? moment(value).format('DD/MM/YYYY') : '',
            },
            { field: 'type', headerName: 'Type', minWidth: 140 },
            {
              field: 'description',
              headerName: 'Description',
              flex: 1,
              minWidth: 220,
            },
            {
              field: 'serviceProvider',
              headerName: 'Provider',
              minWidth: 150,
              flex: 0.5,
            },
            {
              field: 'cost',
              headerName: 'Cost',
              minWidth: 100,
              align: 'right',
              headerAlign: 'right',
              valueGetter: (value) =>
                value === undefined || value === null || value === ''
                  ? null
                  : Number(value),
              renderCell: ({ value }) =>
                value === null ? '' : `${currency}${value.toFixed(2)}`,
            },
            {
              field: 'expiry',
              headerName: 'Valid Until',
              minWidth: 150,
              valueGetter: (value) => (value ? moment(value).toDate() : null),
              renderCell: ({ value }) =>
                value ? (
                  <Chip
                    size="small"
                    label={moment(value).format('DD/MM/YYYY')}
                    color={dateTone(value) ?? 'default'}
                    variant="outlined"
                    sx={{ borderRadius: 1, fontWeight: 700 }}
                  />
                ) : null,
            },
          ]}
          disableRowSelectionOnClick
          slots={{ toolbar: () => null, footer: () => null }}
        />
      )}
    </Stack>
  )
}

export default ServicingSection
